(() => {
  'use strict';

  /*
   * =========================================================
   * BUNKER REGIONAL AVAILABILITY
   * =========================================================
   *
   * LOCATION PRIORITY
   *
   * 1. Manual postcode
   * 2. Confirmed map location
   * 3. Device / GPS location
   *
   * FIRST VISIT:
   *
   * Request browser/device location.
   *
   * If location cannot be accessed:
   * automatically open the Bunker
   * location selector with:
   *
   * - Retry current location
   * - Google Map
   * - Australian postcode
   *
   * This script does NOT modify Zapiet.
   */

  /*
   * =========================================================
   * STORAGE KEYS
   * =========================================================
   */

  const STATE_KEY =
    'bunker_customer_state';

  const POSTCODE_KEY =
    'bunker_customer_postcode';

  const SOURCE_KEY =
    'bunker_location_source';

  const CITY_KEY =
    'bunker_location_city';

  const COUNTRY_KEY =
    'bunker_location_country';

  const COUNTRY_CODE_KEY =
    'bunker_location_country_code';

  const DETECTED_POSTCODE_KEY =
    'bunker_detected_postcode';

  const LAT_KEY =
    'bunker_location_lat';

  const LNG_KEY =
    'bunker_location_lng';

  const GEO_ATTEMPTED_KEY =
    'bunker_geo_prompt_attempted';

  const GEO_ERROR_KEY =
    'bunker_geo_last_error';

  /*
   * =========================================================
   * AUSTRALIAN STATES
   * =========================================================
   */

  const VALID_STATES = [
    'QLD',
    'VIC',
    'TAS',
    'NSW',
    'SA',
    'WA',
    'NT',
    'ACT'
  ];

  const AUSTRALIA_CENTER = {
    lat: -25.2744,
    lng: 133.7751
  };

  /*
   * =========================================================
   * RUNTIME VARIABLES
   * =========================================================
   */

  let googleMapsPromise = null;

  let bunkerMap = null;

  let bunkerGeocoder = null;

  let mapIdleTimer = null;

  let selectedMapLocation = null;

  /*
   * =========================================================
   * PREVENT PRODUCT FLASH
   * =========================================================
   */

  document.documentElement.classList.add(
    'bunker-location-pending'
  );

  const earlyStyle =
    document.createElement('style');

  earlyStyle.id =
    'bunker-regional-early-style';

  earlyStyle.textContent = `
    html.bunker-location-pending
    a.bc-product-card {
      visibility: hidden !important;
    }
  `;

  document.head.appendChild(
    earlyStyle
  );

  /*
   * =========================================================
   * AVAILABLE STATES PARSER
   * =========================================================
   */

  function parseAvailableStates(value) {
    if (!value) {
      return [];
    }

    try {
      const parsed =
        JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed
          .map(state =>
            String(state)
              .trim()
              .toUpperCase()
          )
          .filter(Boolean);
      }
    } catch (error) {
      // Fall back to text parsing.
    }

    return String(value)
      .split(/[,|•]/)
      .map(state =>
        state
          .trim()
          .toUpperCase()
      )
      .filter(Boolean);
  }

  /*
   * =========================================================
   * STORAGE HELPERS
   * =========================================================
   */

  function getSavedState() {
    const state =
      localStorage.getItem(
        STATE_KEY
      );

    if (!state) {
      return null;
    }

    const normalized =
      state
        .trim()
        .toUpperCase();

    return VALID_STATES.includes(
      normalized
    )
      ? normalized
      : null;
  }

  function getSavedPostcode() {
    return (
      localStorage.getItem(
        POSTCODE_KEY
      ) || ''
    ).trim();
  }

  function getDetectedPostcode() {
    return (
      localStorage.getItem(
        DETECTED_POSTCODE_KEY
      ) || ''
    ).trim();
  }

  function getLocationSource() {
    return (
      localStorage.getItem(
        SOURCE_KEY
      ) || ''
    );
  }

  function getSavedCoordinates() {
    const lat =
      parseFloat(
        localStorage.getItem(
          LAT_KEY
        )
      );

    const lng =
      parseFloat(
        localStorage.getItem(
          LNG_KEY
        )
      );

    if (
      Number.isFinite(lat) &&
      Number.isFinite(lng)
    ) {
      return {
        lat,
        lng
      };
    }

    return null;
  }

  function getSavedLocationInfo() {
    return {
      state:
        getSavedState(),

      postcode:
        getSavedPostcode(),

      detectedPostcode:
        getDetectedPostcode(),

      source:
        getLocationSource(),

      city:
        localStorage.getItem(
          CITY_KEY
        ) || '',

      country:
        localStorage.getItem(
          COUNTRY_KEY
        ) || '',

      countryCode:
        localStorage.getItem(
          COUNTRY_CODE_KEY
        ) || '',

      coordinates:
        getSavedCoordinates()
    };
  }

  /*
   * =========================================================
   * POSTCODE → STATE
   * =========================================================
   */

  function getStateFromPostcode(
    postcode
  ) {
    const cleaned =
      String(postcode || '')
        .trim();

    if (
      !/^\d{4}$/.test(cleaned)
    ) {
      return null;
    }

    const number =
      parseInt(
        cleaned,
        10
      );

    /*
     * NT
     */

    if (
      number >= 800 &&
      number <= 999
    ) {
      return 'NT';
    }

    /*
     * ACT
     */

    if (
      cleaned === '0200' ||
      (
        number >= 2600 &&
        number <= 2618
      ) ||
      (
        number >= 2900 &&
        number <= 2920
      )
    ) {
      return 'ACT';
    }

    /*
     * NSW
     */

    if (
      (
        number >= 1000 &&
        number <= 2599
      ) ||
      (
        number >= 2619 &&
        number <= 2899
      ) ||
      (
        number >= 2921 &&
        number <= 2999
      )
    ) {
      return 'NSW';
    }

    /*
     * VIC
     */

    if (
      (
        number >= 3000 &&
        number <= 3999
      ) ||
      (
        number >= 8000 &&
        number <= 8999
      )
    ) {
      return 'VIC';
    }

    /*
     * QLD
     */

    if (
      (
        number >= 4000 &&
        number <= 4999
      ) ||
      (
        number >= 9000 &&
        number <= 9999
      )
    ) {
      return 'QLD';
    }

    /*
     * SA
     */

    if (
      number >= 5000 &&
      number <= 5999
    ) {
      return 'SA';
    }

    /*
     * WA
     */

    if (
      number >= 6000 &&
      number <= 6999
    ) {
      return 'WA';
    }

    /*
     * TAS
     */

    if (
      number >= 7000 &&
      number <= 7999
    ) {
      return 'TAS';
    }

    return null;
  }

  /*
   * =========================================================
   * GOOGLE MAPS LOADER
   * =========================================================
   */

  function loadGoogleMapsAPI() {
    if (
      window.google &&
      window.google.maps &&
      window.google.maps.Map &&
      window.google.maps.Geocoder
    ) {
      return Promise.resolve();
    }

    if (googleMapsPromise) {
      return googleMapsPromise;
    }

    const config =
      window.BunkerRegionalConfig ||
      {};

    const apiKey =
      config.googleMapsApiKey;

    if (!apiKey) {
      return Promise.reject(
        new Error(
          'Google Maps API key is missing.'
        )
      );
    }

    googleMapsPromise =
      new Promise(
        (resolve, reject) => {

          const callbackName =
            '__bunkerGoogleMapsReady';

          window[
            callbackName
          ] = () => {

            try {
              delete window[
                callbackName
              ];
            } catch (error) {
              // Ignore cleanup error.
            }

            resolve();
          };

          const script =
            document.createElement(
              'script'
            );

          script.dataset
            .bunkerGoogleMaps =
            'true';

          script.src =
            'https://maps.googleapis.com/maps/api/js' +
            '?key=' +
            encodeURIComponent(
              apiKey
            ) +
            '&v=weekly' +
            '&loading=async' +
            '&callback=' +
            callbackName;

          script.async = true;

          script.onerror =
            () => {

              googleMapsPromise =
                null;

              reject(
                new Error(
                  'Google Maps failed to load.'
                )
              );
            };

          document.head.appendChild(
            script
          );
        }
      );

    return googleMapsPromise;
  }

  /*
   * =========================================================
   * ADDRESS HELPERS
   * =========================================================
   */

  function findAddressComponent(
    results,
    type
  ) {
    for (
      const result of results
    ) {
      const components =
        result.address_components ||
        [];

      const found =
        components.find(
          component =>
            component.types &&
            component.types.includes(
              type
            )
        );

      if (found) {
        return found;
      }
    }

    return null;
  }

  function normalizeAustralianState(
    shortName,
    longName
  ) {
    const short =
      String(shortName || '')
        .trim()
        .toUpperCase();

    if (
      VALID_STATES.includes(
        short
      )
    ) {
      return short;
    }

    const long =
      String(longName || '')
        .trim()
        .toUpperCase();

    const stateMap = {
      QUEENSLAND:
        'QLD',

      VICTORIA:
        'VIC',

      TASMANIA:
        'TAS',

      'NEW SOUTH WALES':
        'NSW',

      'SOUTH AUSTRALIA':
        'SA',

      'WESTERN AUSTRALIA':
        'WA',

      'NORTHERN TERRITORY':
        'NT',

      'AUSTRALIAN CAPITAL TERRITORY':
        'ACT'
    };

    return (
      stateMap[long] ||
      null
    );
  }

  /*
   * =========================================================
   * REVERSE GEOCODING
   * =========================================================
   */

  async function reverseGeocode(
    latitude,
    longitude
  ) {
    await loadGoogleMapsAPI();

    if (!bunkerGeocoder) {
      bunkerGeocoder =
        new google.maps
          .Geocoder();
    }

    return new Promise(
      (resolve, reject) => {

        bunkerGeocoder.geocode(
          {
            location: {
              lat: latitude,
              lng: longitude
            }
          },

          (
            results,
            status
          ) => {

            if (
              status !== 'OK' ||
              !results ||
              !results.length
            ) {
              reject(
                new Error(
                  'Could not identify this location.'
                )
              );

              return;
            }

            const countryComponent =
              findAddressComponent(
                results,
                'country'
              );

            const stateComponent =
              findAddressComponent(
                results,
                'administrative_area_level_1'
              );

            const postcodeComponent =
              findAddressComponent(
                results,
                'postal_code'
              );

            let cityComponent =
              findAddressComponent(
                results,
                'locality'
              );

            if (!cityComponent) {
              cityComponent =
                findAddressComponent(
                  results,
                  'postal_town'
                );
            }

            if (!cityComponent) {
              cityComponent =
                findAddressComponent(
                  results,
                  'sublocality'
                );
            }

            if (!cityComponent) {
              cityComponent =
                findAddressComponent(
                  results,
                  'administrative_area_level_2'
                );
            }

            const country =
              countryComponent
                ? countryComponent
                    .long_name
                : '';

            const countryCode =
              countryComponent
                ? countryComponent
                    .short_name
                    .toUpperCase()
                : '';

            const city =
              cityComponent
                ? cityComponent
                    .long_name
                : '';

            const postcode =
              postcodeComponent
                ? postcodeComponent
                    .long_name
                : '';

            let state = null;

            if (
              countryCode ===
                'AU' &&
              stateComponent
            ) {
              state =
                normalizeAustralianState(
                  stateComponent
                    .short_name,

                  stateComponent
                    .long_name
                );
            }

            resolve({
              latitude,
              longitude,
              state,
              postcode,
              city,
              country,
              countryCode,

              formattedAddress:
                results[0]
                  .formatted_address ||
                ''
            });
          }
        );
      }
    );
  }

  /*
   * =========================================================
   * PRODUCT METAFIELD → CARD
   * =========================================================
   */

  function attachStatesToProductCards() {
    const stateMap =
      window
        .BunkerRegionalProductStates ||
      {};

    const cards =
      document.querySelectorAll(
        'a.bc-product-card[href*="/products/"]'
      );

    cards.forEach(
      card => {

        try {
          const url =
            new URL(
              card.href,
              window.location.origin
            );

          const match =
            url.pathname.match(
              /\/products\/([^/?#]+)/
            );

          if (!match) {
            return;
          }

          const handle =
            match[1];

          /*
           * FAIL CLOSED:
           * A product missing completely from
           * the regional state map must behave
           * the same as an empty Available States
           * metafield.
           */

          const states =
            Object.prototype
              .hasOwnProperty.call(
                stateMap,
                handle
              )
              ? stateMap[handle]
              : [];

          const value =
            JSON.stringify(
              Array.isArray(states)
                ? states
                : []
            );

          if (
            card.dataset
              .bunkerAvailableStates !==
            value
          ) {
            card.dataset
              .bunkerAvailableStates =
              value;
          }

        } catch (error) {
          console.warn(
            '[Bunker] Could not map product card.',
            error
          );
        }
      }
    );
  }

  /*
   * =========================================================
   * COLLECTION PRODUCT COUNTERS
   * =========================================================
   */

  function updateRegionalProductCounts() {
    const cards =
      document.querySelectorAll(
        'a.bc-product-card[href*="/products/"]'
      );

    if (!cards.length) {
      return;
    }

    const total =
      cards.length;

    const visible =
      [...cards].filter(
        card =>
          !card.hidden
      ).length;

    /*
     * Top:
     * 13 PRODUCTS
     */

    document
      .querySelectorAll(
        '.bc-hero__count'
      )
      .forEach(
        element => {

          element.textContent =
            `${visible} ${
              visible === 1
                ? 'PRODUCT'
                : 'PRODUCTS'
            }`;
        }
      );

    /*
     * Bottom:
     * Showing 13 of 13 products
     */

    document
      .querySelectorAll(
        '.bc-grid-meta__count'
      )
      .forEach(
        element => {

          element.textContent =
            `Showing ${visible} of ${total} products`;
        }
      );
  }

  /*
   * =========================================================
   * LOCATION REQUIRED MESSAGE
   * =========================================================
   */

  function createLocationRequiredMessage() {
    if (
      document.getElementById(
        'bunker-location-required'
      )
    ) {
      return;
    }

    const firstCard =
      document.querySelector(
        'a.bc-product-card'
      );

    if (!firstCard) {
      return;
    }

    const message =
      document.createElement(
        'div'
      );

    message.id =
      'bunker-location-required';

    message.className =
      'bunker-location-required';

    message.innerHTML = `
      <strong>
        Set your delivery location
        to view available products.
      </strong>

      <span>
        Use your current location,
        choose a point on the map,
        or enter an Australian postcode.
      </span>

      <button
        type="button"
        class="bunker-location-required-button"
      >
        Set location
      </button>
    `;

    firstCard.parentElement
      .insertBefore(
        message,
        firstCard
      );

    message
      .querySelector(
        '.bunker-location-required-button'
      )
      .addEventListener(
        'click',
        openLocationPopup
      );
  }

  function showLocationRequiredMessage(
    visible
  ) {
    createLocationRequiredMessage();

    const message =
      document.getElementById(
        'bunker-location-required'
      );

    if (!message) {
      return;
    }

    message.hidden =
      !visible;
  }

  /*
 * =========================================================
 * PRODUCT PAGE REGIONAL STOCK
 * =========================================================
 */

function getRegionalPurchaseStatus(
  form,
  state = getSavedState()
) {
  const variantInput =
    form
      ? form.querySelector(
          '[name="id"]'
        )
      : null;

  const variantId =
    String(
      variantInput
        ? variantInput.value
        : ''
    ).trim();

  const stockMap =
    window.BunkerRegionalVariantStock;

  /*
   * Variant must be identifiable.
   */

  if (!variantId) {
    return {
      allowed: false,
      reason: 'missing_variant',
      variantId
    };
  }

  /*
   * A zero-priced variant is unavailable in every region.
   * Use Shopify's numeric price, never formatted money text.
   */
  const priceMap = window.BunkerRegionalVariantPrices;

  if (
    !priceMap ||
    !Object.prototype.hasOwnProperty.call(priceMap, variantId)
  ) {
    return {
      allowed: false,
      reason: 'missing_variant_price',
      variantId
    };
  }

  const price = priceMap[variantId];

  if (typeof price !== 'number' || !Number.isFinite(price) || price < 0) {
    return {
      allowed: false,
      reason: 'invalid_variant_price',
      variantId
    };
  }

  if (price === 0) {
    return {
      allowed: false,
      reason: 'zero_price',
      variantId
    };
  }

  /*
   * A positive-priced variant still needs a known location.
   */
  if (!state) {
    return {
      allowed: false,
      reason: 'location_required',
      variantId
    };
  }

  /*
   * Regional stock data itself
   * must exist.
   */

  if (
    !stockMap ||
    typeof stockMap !== 'object'
  ) {
    return {
      allowed: false,
      reason: 'missing_regional_data',
      variantId
    };
  }

  /*
   * Selected Shopify variant must
   * exist in the regional stock map.
   */

  if (
    !Object.prototype
      .hasOwnProperty.call(
        stockMap,
        variantId
      )
  ) {
    return {
      allowed: false,
      reason: 'missing_variant_stock',
      variantId
    };
  }

  const regionalStock =
    stockMap[variantId];

  if (
    !regionalStock ||
    typeof regionalStock !== 'object'
  ) {
    return {
      allowed: false,
      reason: 'invalid_regional_data',
      variantId
    };
  }

  /*
   * Selected Australian state must
   * exist explicitly.
   */

  if (
    !Object.prototype
      .hasOwnProperty.call(
        regionalStock,
        state
      )
  ) {
    return {
      allowed: false,
      reason: 'missing_state_stock',
      variantId
    };
  }

  const stock =
    Number(
      regionalStock[state]
    );

  /*
   * Reject malformed values.
   */

  if (!Number.isFinite(stock)) {
    return {
      allowed: false,
      reason: 'invalid_stock',
      variantId
    };
  }

  /*
   * Valid regional stock exists.
   */

  if (stock <= 0) {
    return {
      allowed: false,
      reason: 'sold_out',
      variantId,
      stock
    };
  }

  return {
    allowed: true,
    reason: 'in_stock',
    variantId,
    stock
  };
}


function applyRegionalProductStock(
  state = getSavedState()
) {
  const form =
    document.querySelector(
      'form[action*="/cart/add"]'
    );

  if (!form) {
    return;
  }

  const button =
    form.querySelector(
      '.bp-atc'
    ) ||
    document.querySelector(
      '.bp-atc'
    );

  if (!button) {
    return;
  }

  const status =
    getRegionalPurchaseStatus(
      form,
      state
    );

  form.dataset
    .bunkerRegionalPurchasable =
    status.allowed
      ? '1'
      : '0';

  form.dataset
    .bunkerRegionalReason =
    status.reason;

  /*
   * Positive price and valid regional stock > 0.
   */

  if (status.allowed) {
    button.disabled = false;

    button.removeAttribute(
      'aria-disabled'
    );

    button.textContent =
      'Add to cart';

    return;
  }

  /*
   * Everything else fails closed.
   */

  button.disabled = true;

  button.setAttribute(
    'aria-disabled',
    'true'
  );

  /*
   * True regional zero stock.
   */

  if (
    status.reason ===
      'sold_out'
  ) {
    button.textContent =
      'Sold out';

    return;
  }

  /*
   * No location selected yet.
   */

  if (
    status.reason ===
      'location_required'
  ) {
    button.textContent =
      'Set delivery location';

    return;
  }

  /*
   * Zero price or missing / invalid price or automation data.
   *
   * Do NOT fall back to Shopify's
   * global inventory.
   */

  button.textContent =
    'Unavailable';
}


/*
 * =========================================================
 * HARD REGIONAL PURCHASE GUARD
 * =========================================================
 *
 * Never allow a cart submission when
 * regional validation has not passed.
 */

document.addEventListener(
  'submit',
  event => {

    const form =
      event.target;

    if (
      !form ||
      !form.matches ||
      !form.matches(
        'form[action*="/cart/add"]'
      )
    ) {
      return;
    }

    const status =
      getRegionalPurchaseStatus(
        form,
        getSavedState()
      );

    if (status.allowed) {
      return;
    }

    event.preventDefault();

    event.stopImmediatePropagation();

    applyRegionalProductStock(
      getSavedState()
    );

    console.warn(
      '[Bunker] Add to cart blocked by regional availability.',
      {
        reason:
          status.reason,

        variantId:
          status.variantId,

        state:
          getSavedState()
      }
    );
  },
  true
);


/*
 * Also protect against theme scripts
 * that trigger cart actions directly
 * from the Add to Cart button.
 */

document.addEventListener(
  'click',
  event => {

    const button =
      event.target.closest(
        '.bp-atc'
      );

    if (!button) {
      return;
    }

    const form =
      button.closest(
        'form[action*="/cart/add"]'
      ) ||
      document.querySelector(
        'form[action*="/cart/add"]'
      );

    if (!form) {
      return;
    }

    const status =
      getRegionalPurchaseStatus(
        form,
        getSavedState()
      );

    if (status.allowed) {
      return;
    }

    event.preventDefault();

    event.stopImmediatePropagation();

    applyRegionalProductStock(
      getSavedState()
    );
  },
  true
);

/*
 * =========================================================
 * SHOPIFY COUNTRY SYNC
 * =========================================================
 */

function ensureShopifyAustraliaContext() {
  const currentCountry =
    String(
      window.BunkerShopifyCountry || ''
    )
      .trim()
      .toUpperCase();

  /*
   * Shopify is already using
   * Australia. Nothing to do.
   */

  if (currentCountry === 'AU') {
    return false;
  }

  const form =
    document.getElementById(
      'bunker-shopify-localization-form'
    );

  if (!form) {
    console.warn(
      '[Bunker] Shopify localization form not found.'
    );

    return false;
  }

  const countryInput =
    form.querySelector(
      '[name="country_code"]'
    );

  if (!countryInput) {
    console.warn(
      '[Bunker] Shopify country input not found.'
    );

    return false;
  }

  /*
   * Customer has selected an
   * Australian Bunker location,
   * so switch Shopify itself to AU.
   */

  countryInput.value = 'AU';

  const returnInput =
    form.querySelector(
      '[name="return_to"]'
    );

  if (returnInput) {
    const url =
      new URL(
        window.location.href
      );

    /*
     * Remove our temporary testing
     * ?country=AU parameter.
     */

    url.searchParams.delete(
      'country'
    );

    returnInput.value =
      url.pathname +
      url.search +
      url.hash;
  }

  console.log(
    '[Bunker] Switching Shopify country:',
    {
      from:
        currentCountry || 'unknown',
      to:
        'AU'
    }
  );

  form.submit();

  return true;
}

  /*
   * =========================================================
   * FILTER PRODUCTS
   * =========================================================
   */

  function applyRegionalAvailability(
    state = getSavedState()
  ) {
    attachStatesToProductCards();

      /*
   * Any valid Australian state means
   * Shopify should also use AU.
   *
   * If Shopify needs changing, the
   * localization form submits and the
   * page reloads once.
   */

  if (
    state &&
    ensureShopifyAustraliaContext()
  ) {
    return;
  }

      applyRegionalProductStock(
    state
  );

    const cards =
      document.querySelectorAll(
        '[data-bunker-available-states]'
      );

    /*
     * No AU location:
     * hide products.
     */

    if (!state) {
      cards.forEach(
        card => {
          card.hidden = true;
        }
      );

      showLocationRequiredMessage(
        cards.length > 0
      );

      updateRegionalProductCounts();

      document.documentElement
        .classList.remove(
          'bunker-location-pending'
        );

      return;
    }

    /*
     * AU state known.
     */

    cards.forEach(
      card => {

        const states =
          parseAvailableStates(
            card.dataset
              .bunkerAvailableStates
          );

/*
 * FAIL CLOSED:
 * If Available States is missing,
 * blank or invalid, do not expose
 * the product on the storefront.
 */

if (!states.length) {
  card.hidden = true;
  return;
}

        card.hidden =
          !states.includes(
            state
          );
      }
    );

    showLocationRequiredMessage(
      false
    );

    updateRegionalProductCounts();

    document.documentElement
      .classList.remove(
        'bunker-location-pending'
      );
  }

  /*
   * =========================================================
   * SAVE DETECTED LOCATION
   * =========================================================
   */

  function saveDetectedLocation(
    location,
    source
  ) {
    localStorage.setItem(
      SOURCE_KEY,
      source
    );

    localStorage.setItem(
      CITY_KEY,
      location.city || ''
    );

    localStorage.setItem(
      COUNTRY_KEY,
      location.country || ''
    );

    localStorage.setItem(
      COUNTRY_CODE_KEY,
      location.countryCode || ''
    );

    localStorage.setItem(
      DETECTED_POSTCODE_KEY,
      location.postcode || ''
    );

    if (
      Number.isFinite(
        location.latitude
      )
    ) {
      localStorage.setItem(
        LAT_KEY,
        String(
          location.latitude
        )
      );
    }

    if (
      Number.isFinite(
        location.longitude
      )
    ) {
      localStorage.setItem(
        LNG_KEY,
        String(
          location.longitude
        )
      );
    }

    /*
     * GPS/map replaces manual postcode.
     */

    localStorage.removeItem(
      POSTCODE_KEY
    );

    if (
      location.countryCode ===
        'AU' &&
      location.state
    ) {
      localStorage.setItem(
        STATE_KEY,
        location.state
      );
    } else {
      localStorage.removeItem(
        STATE_KEY
      );
    }

    localStorage.removeItem(
      GEO_ERROR_KEY
    );
  }

  /*
   * =========================================================
   * DEVICE COORDINATES
   * =========================================================
   */

  function getDeviceCoordinates() {
    return new Promise(
      (resolve, reject) => {

        if (
          !navigator.geolocation
        ) {
          reject({
            code: 0,

            message:
              'Geolocation is not supported.'
          });

          return;
        }

        navigator.geolocation
          .getCurrentPosition(

            position => {

              resolve({
                latitude:
                  position.coords
                    .latitude,

                longitude:
                  position.coords
                    .longitude,

                accuracy:
                  position.coords
                    .accuracy
              });
            },

            error => {
              reject(error);
            },

            {
              enableHighAccuracy:
                true,

              timeout:
                15000,

              maximumAge:
                300000
            }
          );
      }
    );
  }

  /*
   * =========================================================
   * GEOLOCATION ERROR TYPE
   * =========================================================
   */

  function saveGeolocationError(
    error
  ) {
    let value =
      'unavailable';

    if (
      error &&
      error.code === 1
    ) {
      value =
        'permission_denied';
    }

    if (
      error &&
      error.code === 2
    ) {
      value =
        'position_unavailable';
    }

    if (
      error &&
      error.code === 3
    ) {
      value =
        'timeout';
    }

    localStorage.setItem(
      GEO_ERROR_KEY,
      value
    );

    return value;
  }

  /*
   * =========================================================
   * DEVICE / GPS LOCATION
   * =========================================================
   */

  async function requestPreciseLocation() {
    localStorage.setItem(
      GEO_ATTEMPTED_KEY,
      '1'
    );

    setLocationBarLoading(
      'Waiting for location permission…'
    );

    try {
      const coordinates =
        await getDeviceCoordinates();

      console.log(
        '[Bunker] Device coordinates received.',
        {
          accuracy:
            coordinates.accuracy
        }
      );

      setLocationBarLoading(
        'Finding your location…'
      );

      const location =
        await reverseGeocode(
          coordinates.latitude,
          coordinates.longitude
        );

      const source =
        location.countryCode ===
          'AU'
          ? 'gps'
          : 'gps_outside_au';

      saveDetectedLocation(
        location,
        source
      );

      applyRegionalAvailability(
        location.state
      );

      updateLocationBar();

      document.dispatchEvent(
        new CustomEvent(
          'bunker:location-changed',
          {
            detail: {
              ...location,
              source
            }
          }
        )
      );

      console.log(
        '[Bunker] Device location:',
        location
      );

      return location;

    } catch (error) {

      const errorType =
        saveGeolocationError(
          error
        );

      console.warn(
        '[Bunker] Location request failed:',
        error
      );

      localStorage.removeItem(
        STATE_KEY
      );

      applyRegionalAvailability(
        null
      );

      updateLocationBar();

      /*
       * =====================================================
       * MOBILE / PERMISSION FALLBACK
       * =====================================================
       *
       * Some phones/browsers do not
       * display the native permission
       * prompt if location has already
       * been disabled in browser/device
       * settings.
       *
       * In that case, automatically
       * open our own map/postcode
       * selector.
       */

      const overlay =
        document.getElementById(
          'bunker-location-overlay'
        );

      if (
        overlay &&
        overlay.hidden
      ) {

        setTimeout(
          async () => {

            await openLocationPopup();

            /*
             * Show a helpful reason
             * inside our modal.
             */

            if (
              errorType ===
              'permission_denied'
            ) {

              showPopupError(
                'We could not access your current location. Location permission may be disabled in your browser or phone settings. You can try again, choose your location on the map, or enter your Australian postcode.'
              );

            } else if (
              errorType ===
              'timeout'
            ) {

              showPopupError(
                'Your location request took too long. You can try again, choose your location on the map, or enter your Australian postcode.'
              );

            } else if (
              errorType ===
              'position_unavailable'
            ) {

              showPopupError(
                'Your device could not provide a location. You can move the map to your delivery area or enter your Australian postcode.'
              );

            } else {

              showPopupError(
                'We could not determine your current location. You can choose your location on the map or enter your Australian postcode.'
              );
            }

          },
          300
        );
      }

      return {
        error:
          errorType
      };
    }
  }

  /*
   * =========================================================
   * MANUAL POSTCODE
   * =========================================================
   */

  function setStateFromPostcode(
    postcode
  ) {
    const cleaned =
      String(postcode || '')
        .trim();

    const state =
      getStateFromPostcode(
        cleaned
      );

    if (!state) {
      return null;
    }

    localStorage.setItem(
      STATE_KEY,
      state
    );

    localStorage.setItem(
      POSTCODE_KEY,
      cleaned
    );

    localStorage.setItem(
      SOURCE_KEY,
      'postcode'
    );

    /*
     * Remove previous GPS/map data.
     */

    [
      CITY_KEY,
      COUNTRY_KEY,
      COUNTRY_CODE_KEY,
      DETECTED_POSTCODE_KEY,
      LAT_KEY,
      LNG_KEY,
      GEO_ERROR_KEY
    ].forEach(
      key =>
        localStorage
          .removeItem(key)
    );

    applyRegionalAvailability(
      state
    );

    updateLocationBar();

    document.dispatchEvent(
      new CustomEvent(
        'bunker:location-changed',
        {
          detail: {
            state,

            postcode:
              cleaned,

            source:
              'postcode'
          }
        }
      )
    );

    return state;
  }

  /*
   * =========================================================
   * STYLES
   * =========================================================
   */

  function injectStyles() {
    if (
      document.getElementById(
        'bunker-regional-styles'
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.id =
      'bunker-regional-styles';

    style.textContent = `

      /*
       * LOCATION BAR
       */

      .bunker-location-bar {
        width: 100%;
        position: relative;
        z-index: 40;
        background: #171717;
        border-top:
          1px solid
          rgba(255,255,255,.07);
        border-bottom:
          1px solid
          rgba(255,255,255,.1);
        color: #fff;
      }

      /* Keep custom collection filters below the complete sticky header stack. */
      .bc-filters {
        top: var(--bunker-collection-filter-top, 0px) !important;
        z-index: 20 !important;
      }

      .bunker-location-bar-inner {
        width: 100%;
        max-width: 1440px;
        min-height: 42px;
        margin: 0 auto;
        padding: 8px 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        font-size: 13px;
        line-height: 1.35;
        text-align: center;
      }

      .bunker-location-pin-small {
        color: #d67821;
        font-size: 17px;
        line-height: 1;
      }

      .bunker-location-primary {
        font-weight: 700;
      }

      .bunker-location-secondary {
        color:
          rgba(255,255,255,.65);
      }

      .bunker-location-separator {
        color:
          rgba(255,255,255,.3);
      }

      .bunker-location-action {
        padding: 0;
        border: 0;
        background: transparent;
        color: #df7a21;
        cursor: pointer;
        font: inherit;
        font-weight: 700;
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      /*
       * COLLECTION MESSAGE
       */

      .bunker-location-required {
        grid-column: 1 / -1;
        width: 100%;
        padding: 32px 22px;
        margin: 12px 0 26px;
        background: #f4f4f4;
        border:
          1px solid #dedede;
        text-align: center;
        color: #171717;
      }

      .bunker-location-required[hidden] {
        display: none !important;
      }

      .bunker-location-required strong {
        display: block;
        margin-bottom: 7px;
        font-size: 18px;
      }

      .bunker-location-required span {
        display: block;
        margin-bottom: 17px;
        font-size: 14px;
        color: #666;
      }

      .bunker-location-required-button {
        min-height: 44px;
        padding: 0 21px;
        border: 0;
        background: #c76b19;
        color: #fff;
        cursor: pointer;
        font-weight: 700;
        text-transform: uppercase;
      }

      /*
       * MODAL
       */

      .bunker-location-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 18px;
        background:
          rgba(0,0,0,.72);
        backdrop-filter:
          blur(3px);
      }

      .bunker-location-overlay[hidden] {
        display: none !important;
      }

      .bunker-location-modal {
        position: relative;
        width: 100%;
        max-width: 600px;
        max-height:
          calc(100vh - 36px);
        overflow-y: auto;
        padding: 30px;
        background: #111;
        color: #fff;
        border:
          1px solid
          rgba(255,255,255,.14);
        box-shadow:
          0 24px 70px
          rgba(0,0,0,.45);
      }

      .bunker-location-close {
        position: absolute;
        top: 10px;
        right: 12px;
        z-index: 5;
        width: 36px;
        height: 36px;
        padding: 0;
        border: 0;
        background: transparent;
        color:
          rgba(255,255,255,.72);
        cursor: pointer;
        font-size: 27px;
        line-height: 1;
      }

      .bunker-location-eyebrow {
        margin: 0 0 8px;
        color: #c76b19;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: .12em;
        text-transform: uppercase;
      }

      .bunker-location-title {
        margin: 0 0 10px;
        color: #fff;
        font-size:
          clamp(25px,5vw,34px);
        font-weight: 800;
        line-height: 1;
        text-transform: uppercase;
      }

      .bunker-location-copy {
        margin: 0 0 18px;
        color:
          rgba(255,255,255,.68);
        font-size: 14px;
        line-height: 1.5;
      }

      /*
       * CURRENT LOCATION
       */

      .bunker-use-current-location {
        width: 100%;
        min-height: 48px;
        margin-bottom: 16px;
        padding: 0 18px;
        border:
          1px solid #c76b19;
        background: transparent;
        color: #fff;
        cursor: pointer;
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
      }

      .bunker-use-current-location:hover {
        background:
          rgba(199,107,25,.15);
      }

      .bunker-use-current-location:disabled {
        opacity: .5;
        cursor: wait;
      }

      /*
       * GOOGLE MAP
       */

      .bunker-map-wrapper {
        position: relative;
        width: 100%;
        height: 310px;
        overflow: hidden;
        background: #2a2a2a;
      }

      #bunker-location-map {
        position: absolute;
        inset: 0;
      }

      /*
       * CENTER PIN
       */

      .bunker-map-center-pin {
        position: absolute;
        left: 50%;
        top: 50%;
        z-index: 10;
        width: 30px;
        height: 30px;
        pointer-events: none;
        background: #d67821;
        border:
          3px solid #fff;
        border-radius:
          50% 50% 50% 0;
        box-shadow:
          0 3px 10px
          rgba(0,0,0,.35);
        transform:
          translate(-50%,-100%)
          rotate(-45deg);
      }

      .bunker-map-center-pin::after {
        content: "";
        position: absolute;
        width: 8px;
        height: 8px;
        top: 8px;
        left: 8px;
        background: #fff;
        border-radius: 50%;
      }

      .bunker-map-help {
        margin: 8px 0 0;
        color:
          rgba(255,255,255,.55);
        font-size: 12px;
        text-align: center;
      }

      /*
       * MAP RESULT
       */

      .bunker-map-result {
        margin-top: 14px;
        padding: 14px;
        background:
          rgba(255,255,255,.06);
        border:
          1px solid
          rgba(255,255,255,.1);
      }

      .bunker-map-result-label {
        display: block;
        margin-bottom: 5px;
        color:
          rgba(255,255,255,.5);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .1em;
        text-transform: uppercase;
      }

      .bunker-map-result-location {
        display: block;
        color: #fff;
        font-size: 15px;
        font-weight: 700;
        line-height: 1.4;
      }

      .bunker-map-result-warning {
        display: none;
        margin-top: 6px;
        color: #ffab76;
        font-size: 12px;
        line-height: 1.4;
      }

      .bunker-map-result-warning.is-visible {
        display: block;
      }

      /*
       * CONFIRM MAP
       */

      .bunker-confirm-map-location {
        width: 100%;
        min-height: 50px;
        margin-top: 12px;
        padding: 0 18px;
        border: 0;
        background: #c76b19;
        color: #fff;
        cursor: pointer;
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
      }

      .bunker-confirm-map-location:disabled {
        opacity: .38;
        cursor: not-allowed;
      }

      /*
       * ERROR
       */

      .bunker-location-error {
        display: none;
        margin: 14px 0;
        padding: 12px;
        background:
          rgba(255,120,100,.08);
        border:
          1px solid
          rgba(255,150,142,.3);
        color: #ffb0a9;
        font-size: 13px;
        line-height: 1.45;
      }

      .bunker-location-error.is-visible {
        display: block;
      }

      /*
       * SEPARATOR
       */

      .bunker-location-or {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 20px 0;
        color:
          rgba(255,255,255,.42);
        font-size: 10px;
        text-transform: uppercase;
      }

      .bunker-location-or::before,
      .bunker-location-or::after {
        content: "";
        flex: 1;
        height: 1px;
        background:
          rgba(255,255,255,.14);
      }

      /*
       * POSTCODE
       */

      .bunker-location-form {
        display: flex;
        gap: 10px;
      }

      .bunker-location-input {
        min-width: 0;
        flex: 1;
        height: 50px;
        padding: 0 15px;
        border:
          1px solid
          rgba(255,255,255,.28);
        outline: none;
        background: #fff;
        color: #111;
        font-size: 16px;
      }

      .bunker-location-submit {
        min-height: 50px;
        padding: 0 22px;
        border: 0;
        cursor: pointer;
        background: #c76b19;
        color: #fff;
        font-size: 13px;
        font-weight: 800;
        text-transform: uppercase;
      }

      .bunker-location-note {
        margin: 14px 0 0;
        color:
          rgba(255,255,255,.45);
        font-size: 11px;
        line-height: 1.45;
      }

      /*
       * MOBILE
       */

      @media (
        max-width: 749px
      ) {

        .bunker-location-bar-inner {
          min-height: 40px;
          padding: 7px 10px;
          gap: 7px;
          font-size: 12px;
        }

        .bunker-location-secondary,
        .bunker-location-separator {
          display: none;
        }

        .bunker-location-modal {
          padding:
            27px 18px 20px;
        }

        .bunker-map-wrapper {
          height: 260px;
        }

        .bunker-location-form {
          flex-direction: column;
        }

        .bunker-location-submit {
          width: 100%;
        }
      }
    `;

    document.head.appendChild(
      style
    );
  }

  /*
   * =========================================================
   * HEADER LOCATION BAR
   * =========================================================
   */

  let collectionFilterResizeObserver = null;
  let collectionFilterHeaderObserver = null;

  function syncCollectionFilterStickyOffset() {
    const root = document.documentElement;
    const filterBar = document.querySelector('.bc-filters');
    const headerSection = document.querySelector('.cc-header');

    if (!filterBar || !headerSection) {
      root.style.removeProperty('--bunker-collection-filter-top');
      return;
    }

    const headerIsSticky =
      headerSection.classList.contains('cc-header--sticky') ||
      window.getComputedStyle(headerSection).position === 'sticky';

    const stickyTop = headerIsSticky
      ? Math.ceil(headerSection.getBoundingClientRect().height)
      : 0;

    root.style.setProperty(
      '--bunker-collection-filter-top',
      `${stickyTop}px`
    );
  }

  function initCollectionFilterStickyOffset() {
    const filterBar = document.querySelector('.bc-filters');
    const headerSection = document.querySelector('.cc-header');

    if (!filterBar || !headerSection) {
      return;
    }

    syncCollectionFilterStickyOffset();

    if ('ResizeObserver' in window) {
      collectionFilterResizeObserver = new ResizeObserver(
        syncCollectionFilterStickyOffset
      );
      collectionFilterResizeObserver.observe(headerSection);
    }

    collectionFilterHeaderObserver = new MutationObserver(
      syncCollectionFilterStickyOffset
    );
    collectionFilterHeaderObserver.observe(headerSection, {
      attributes: true,
      attributeFilter: ['class']
    });

    window.addEventListener(
      'resize',
      syncCollectionFilterStickyOffset,
      { passive: true }
    );

    window.requestAnimationFrame(syncCollectionFilterStickyOffset);
  }

  function createLocationBar() {
    if (
      document.getElementById(
        'bunker-location-bar'
      )
    ) {
      return;
    }

    const bar =
      document.createElement(
        'div'
      );

    bar.id =
      'bunker-location-bar';

    bar.className =
      'bunker-location-bar';

    bar.innerHTML = `
      <div
        class="bunker-location-bar-inner"
      >

        <span
          class="bunker-location-pin-small"
          aria-hidden="true"
        >
          ●
        </span>

        <span
          class="bunker-location-primary"
        >
          Checking your location…
        </span>

        <span
          class="bunker-location-secondary"
        ></span>

        <span
          class="bunker-location-separator"
        >
          |
        </span>

        <button
          type="button"
          class="bunker-location-action"
        >
          Change
        </button>

      </div>
    `;

    const header =
      document.querySelector(
        'header'
      );

    if (header) {
      header.insertAdjacentElement(
        'afterend',
        bar
      );
    } else {
      document.body.insertBefore(
        bar,
        document.body.firstChild
      );
    }

    bar
      .querySelector(
        '.bunker-location-action'
      )
      .addEventListener(
        'click',
        openLocationPopup
      );
  }

  function setLocationBarLoading(
    text
  ) {
    const bar =
      document.getElementById(
        'bunker-location-bar'
      );

    if (!bar) {
      return;
    }

    bar.querySelector(
      '.bunker-location-primary'
    ).textContent =
      text;

    bar.querySelector(
      '.bunker-location-secondary'
    ).textContent =
      '';

    bar.querySelector(
      '.bunker-location-action'
    ).textContent =
      'Set manually';
  }

  function updateLocationBar() {
    const bar =
      document.getElementById(
        'bunker-location-bar'
      );

    if (!bar) {
      return;
    }

    const primary =
      bar.querySelector(
        '.bunker-location-primary'
      );

    const secondary =
      bar.querySelector(
        '.bunker-location-secondary'
      );

    const action =
      bar.querySelector(
        '.bunker-location-action'
      );

    const info =
      getSavedLocationInfo();

    /*
     * Manual postcode
     */

    if (
      info.source ===
        'postcode' &&
      info.state &&
      info.postcode
    ) {
      primary.textContent =
        `Delivery location: ${info.postcode}, ${info.state}`;

      secondary.textContent =
        'Showing your local range';

      action.textContent =
        'Change';

      return;
    }

    /*
     * Map
     */

    if (
      info.source ===
        'map' &&
      info.state
    ) {
      let locationText =
        info.city ||
        info.state;

      if (
        info.city &&
        info.state
      ) {
        locationText =
          `${info.city}, ${info.state}`;
      }

      if (
        info.detectedPostcode
      ) {
        locationText +=
          ` ${info.detectedPostcode}`;
      }

      primary.textContent =
        `Delivery location: ${locationText}`;

      secondary.textContent =
        'Showing your local range';

      action.textContent =
        'Change';

      return;
    }

    /*
     * Australian GPS
     */

    if (
      info.source === 'gps' &&
      info.state
    ) {
      const locationText =
        info.city
          ? `${info.city}, ${info.state}`
          : info.state;

      primary.textContent =
        `Location: ${locationText}`;

      secondary.textContent =
        `Showing ${info.state} range`;

      action.textContent =
        'Change';

      return;
    }

    /*
     * Outside Australia
     */

    if (
      info.source ===
      'gps_outside_au'
    ) {
      const pieces = [];

      if (info.city) {
        pieces.push(
          info.city
        );
      }

      if (info.country) {
        pieces.push(
          info.country
        );
      }

      primary.textContent =
        pieces.length
          ? `Location: ${pieces.join(', ')}`
          : 'Outside Australia';

      secondary.textContent =
        'Choose an Australian delivery location';

      action.textContent =
        'Change location';

      return;
    }

    /*
     * Error
     */

    const geoError =
      localStorage.getItem(
        GEO_ERROR_KEY
      );

    if (
      geoError ===
      'permission_denied'
    ) {
      primary.textContent =
        'Location access blocked';

      secondary.textContent =
        'Choose an Australian delivery location';

      action.textContent =
        'Set location';

      return;
    }

    if (geoError) {
      primary.textContent =
        'Location unavailable';

      secondary.textContent =
        'Choose an Australian delivery location';

      action.textContent =
        'Set location';

      return;
    }

    primary.textContent =
      'Set your delivery location';

    secondary.textContent =
      'Location is required to view available products';

    action.textContent =
      'Set location';
  }

  /*
   * =========================================================
   * LOCATION MODAL
   * =========================================================
   */

  function createLocationPopup() {
    if (
      document.getElementById(
        'bunker-location-overlay'
      )
    ) {
      return;
    }

    const overlay =
      document.createElement(
        'div'
      );

    overlay.id =
      'bunker-location-overlay';

    overlay.className =
      'bunker-location-overlay';

    overlay.hidden = true;

    overlay.innerHTML = `
      <div
        class="bunker-location-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bunker-location-title"
      >

        <button
          type="button"
          class="bunker-location-close"
          aria-label="Close"
        >
          ×
        </button>

        <p
          class="bunker-location-eyebrow"
        >
          Delivery location
        </p>

        <h2
          id="bunker-location-title"
          class="bunker-location-title"
        >
          Choose your location
        </h2>

        <p
          class="bunker-location-copy"
        >
          Use your current location,
          move the map to your delivery
          area, or enter an Australian
          postcode.
        </p>

        <button
          type="button"
          class="bunker-use-current-location"
        >
          Use my current location
        </button>

        <p
          class="bunker-location-error"
          role="alert"
        ></p>

        <div
          class="bunker-map-wrapper"
        >

          <div
            id="bunker-location-map"
          ></div>

          <div
            class="bunker-map-center-pin"
            aria-hidden="true"
          ></div>

        </div>

        <p
          class="bunker-map-help"
        >
          Move the map until the pin
          is over your delivery area.
        </p>

        <div
          class="bunker-map-result"
        >

          <span
            class="bunker-map-result-label"
          >
            Selected location
          </span>

          <span
            class="bunker-map-result-location"
          >
            Loading map…
          </span>

          <span
            class="bunker-map-result-warning"
          ></span>

        </div>

        <button
          type="button"
          class="bunker-confirm-map-location"
          disabled
        >
          Use this location
        </button>

        <div
          class="bunker-location-or"
        >
          or enter postcode
        </div>

        <form
          class="bunker-location-form"
          novalidate
        >

          <input
            class="bunker-location-input"
            type="text"
            inputmode="numeric"
            autocomplete="postal-code"
            maxlength="4"
            pattern="[0-9]{4}"
            placeholder="Australian postcode"
            aria-label="Australian postcode"
          >

          <button
            class="bunker-location-submit"
            type="submit"
          >
            Apply
          </button>

        </form>

      </div>
    `;

    document.body.appendChild(
      overlay
    );

    /*
     * Close
     */

    overlay
      .querySelector(
        '.bunker-location-close'
      )
      .addEventListener(
        'click',
        closeLocationPopup
      );

    overlay.addEventListener(
      'click',
      event => {

        if (
          event.target ===
          overlay
        ) {
          closeLocationPopup();
        }
      }
    );

    /*
     * Use current location
     */

    const currentButton =
      overlay.querySelector(
        '.bunker-use-current-location'
      );

    currentButton
      .addEventListener(
        'click',
        async () => {

          hidePopupError();

          currentButton.disabled =
            true;

          currentButton.textContent =
            'Finding location…';

          try {
            const coordinates =
              await getDeviceCoordinates();

            await ensureMapReady();

            bunkerMap.setZoom(
              15
            );

            bunkerMap.setCenter({
              lat:
                coordinates.latitude,

              lng:
                coordinates.longitude
            });

          } catch (error) {

            const type =
              saveGeolocationError(
                error
              );

            if (
              type ===
              'permission_denied'
            ) {

              showPopupError(
                'Location access is blocked. You can enable location permission in your browser or phone settings, move the map manually, or enter your Australian postcode.'
              );

            } else {

              showPopupError(
                'We could not access your current location. Move the map manually or enter your Australian postcode.'
              );
            }
          }

          currentButton.disabled =
            false;

          currentButton.textContent =
            'Use my current location';
        }
      );

    /*
     * Confirm map
     */

    overlay
      .querySelector(
        '.bunker-confirm-map-location'
      )
      .addEventListener(
        'click',
        confirmMapLocation
      );

    /*
     * Postcode input
     */

    const input =
      overlay.querySelector(
        '.bunker-location-input'
      );

    input.addEventListener(
      'input',
      () => {

        input.value =
          input.value
            .replace(/\D/g, '')
            .slice(0, 4);

        hidePopupError();
      }
    );

    /*
     * Postcode submit
     */

    overlay
      .querySelector(
        '.bunker-location-form'
      )
      .addEventListener(
        'submit',
        event => {

          event.preventDefault();

          const postcode =
            input.value.trim();

          const state =
            setStateFromPostcode(
              postcode
            );

          if (!state) {
            showPopupError(
              'Please enter a valid 4-digit Australian postcode.'
            );

            input.focus();

            return;
          }

          closeLocationPopup();
        }
      );
  }

  /*
   * =========================================================
   * MAP INITIALISATION
   * =========================================================
   */

  async function ensureMapReady() {
    await loadGoogleMapsAPI();

    if (!bunkerGeocoder) {
      bunkerGeocoder =
        new google.maps
          .Geocoder();
    }

    if (bunkerMap) {
      return bunkerMap;
    }

    const mapElement =
      document.getElementById(
        'bunker-location-map'
      );

    if (!mapElement) {
      throw new Error(
        'Map element missing.'
      );
    }

    const initial =
      await getInitialMapPosition();

    bunkerMap =
      new google.maps.Map(
        mapElement,
        {
          center:
            initial.center,

          zoom:
            initial.zoom,

          mapTypeControl:
            false,

          streetViewControl:
            false,

          fullscreenControl:
            false,

          zoomControl:
            true,

          clickableIcons:
            false,

          gestureHandling:
            'greedy'
        }
      );

    /*
     * Moving
     */

    bunkerMap.addListener(
      'dragstart',
      () => {

        selectedMapLocation =
          null;

        setMapResultLoading(
          'Finding location…'
        );
      }
    );

    /*
     * Stopped moving
     */

    bunkerMap.addListener(
      'idle',
      () => {

        clearTimeout(
          mapIdleTimer
        );

        mapIdleTimer =
          setTimeout(
            updateMapLocationFromCenter,
            350
          );
      }
    );

    return bunkerMap;
  }

  /*
   * =========================================================
   * INITIAL MAP POSITION
   * =========================================================
   */

  async function getInitialMapPosition() {
    const coordinates =
      getSavedCoordinates();

    if (coordinates) {
      return {
        center:
          coordinates,

        zoom:
          15
      };
    }

    const postcode =
      getSavedPostcode();

    if (postcode) {

      try {
        await loadGoogleMapsAPI();

        if (!bunkerGeocoder) {
          bunkerGeocoder =
            new google.maps
              .Geocoder();
        }

        const location =
          await geocodeAddress(
            `${postcode}, Australia`
          );

        if (location) {
          return {
            center:
              location,

            zoom:
              13
          };
        }

      } catch (error) {
        // Fall back to Australia.
      }
    }

    return {
      center:
        AUSTRALIA_CENTER,

      zoom:
        4
    };
  }

  /*
   * =========================================================
   * FORWARD GEOCODE
   * =========================================================
   */

  function geocodeAddress(
    address
  ) {
    return new Promise(
      (resolve, reject) => {

        bunkerGeocoder.geocode(
          {
            address
          },

          (
            results,
            status
          ) => {

            if (
              status !== 'OK' ||
              !results ||
              !results.length
            ) {
              reject(
                new Error(
                  'Address not found.'
                )
              );

              return;
            }

            const location =
              results[0]
                .geometry
                .location;

            resolve({
              lat:
                location.lat(),

              lng:
                location.lng()
            });
          }
        );
      }
    );
  }

  /*
   * =========================================================
   * MAP CENTER → LOCATION
   * =========================================================
   */

  async function updateMapLocationFromCenter() {
    if (!bunkerMap) {
      return;
    }

    const center =
      bunkerMap.getCenter();

    if (!center) {
      return;
    }

    const latitude =
      center.lat();

    const longitude =
      center.lng();

    setMapResultLoading(
      'Finding location…'
    );

    try {
      const location =
        await reverseGeocode(
          latitude,
          longitude
        );

      selectedMapLocation =
        location;

      updateMapResult(
        location
      );

    } catch (error) {

      selectedMapLocation =
        null;

      setMapResultError(
        'We could not identify this location.'
      );
    }
  }

  /*
   * =========================================================
   * MAP RESULT UI
   * =========================================================
   */

  function setMapResultLoading(
    text
  ) {
    const locationElement =
      document.querySelector(
        '.bunker-map-result-location'
      );

    const warning =
      document.querySelector(
        '.bunker-map-result-warning'
      );

    const confirmButton =
      document.querySelector(
        '.bunker-confirm-map-location'
      );

    if (locationElement) {
      locationElement.textContent =
        text;
    }

    if (warning) {
      warning.textContent =
        '';

      warning.classList.remove(
        'is-visible'
      );
    }

    if (confirmButton) {
      confirmButton.disabled =
        true;
    }
  }

  function setMapResultError(
    text
  ) {
    const locationElement =
      document.querySelector(
        '.bunker-map-result-location'
      );

    const confirmButton =
      document.querySelector(
        '.bunker-confirm-map-location'
      );

    if (locationElement) {
      locationElement.textContent =
        text;
    }

    if (confirmButton) {
      confirmButton.disabled =
        true;
    }
  }

  function updateMapResult(
    location
  ) {
    const locationElement =
      document.querySelector(
        '.bunker-map-result-location'
      );

    const warning =
      document.querySelector(
        '.bunker-map-result-warning'
      );

    const confirmButton =
      document.querySelector(
        '.bunker-confirm-map-location'
      );

    if (
      !locationElement ||
      !warning ||
      !confirmButton
    ) {
      return;
    }

    const parts = [];

    if (location.city) {
      parts.push(
        location.city
      );
    }

    if (
      location.countryCode ===
        'AU' &&
      location.state
    ) {
      parts.push(
        location.state
      );
    }

    if (location.postcode) {
      parts.push(
        location.postcode
      );
    }

    if (
      location.country &&
      location.countryCode !==
        'AU'
    ) {
      parts.push(
        location.country
      );
    }

    locationElement.textContent =
      parts.length
        ? parts.join(', ')
        : location.formattedAddress;

    /*
     * Australia only.
     */

    if (
      location.countryCode ===
        'AU' &&
      location.state
    ) {
      warning.textContent =
        '';

      warning.classList.remove(
        'is-visible'
      );

      confirmButton.disabled =
        false;

      confirmButton.textContent =
        'Use this location';

      return;
    }

    warning.textContent =
      'This location is outside Australia. Move the map to an Australian delivery area or enter an Australian postcode below.';

    warning.classList.add(
      'is-visible'
    );

    confirmButton.disabled =
      true;
  }

  /*
   * =========================================================
   * CONFIRM MAP
   * =========================================================
   */

  function confirmMapLocation() {
    if (
      !selectedMapLocation ||
      selectedMapLocation
        .countryCode !== 'AU' ||
      !selectedMapLocation
        .state
    ) {
      return;
    }

    saveDetectedLocation(
      selectedMapLocation,
      'map'
    );

    applyRegionalAvailability(
      selectedMapLocation
        .state
    );

    updateLocationBar();

    closeLocationPopup();

    document.dispatchEvent(
      new CustomEvent(
        'bunker:location-changed',
        {
          detail: {
            ...selectedMapLocation,
            source:
              'map'
          }
        }
      )
    );

    console.log(
      '[Bunker] Map location confirmed:',
      selectedMapLocation
    );
  }

  /*
   * =========================================================
   * POPUP ERRORS
   * =========================================================
   */

  function showPopupError(
    message
  ) {
    const error =
      document.querySelector(
        '.bunker-location-error'
      );

    if (!error) {
      return;
    }

    error.textContent =
      message;

    error.classList.add(
      'is-visible'
    );
  }

  function hidePopupError() {
    const error =
      document.querySelector(
        '.bunker-location-error'
      );

    if (!error) {
      return;
    }

    error.textContent =
      '';

    error.classList.remove(
      'is-visible'
    );
  }

  /*
   * =========================================================
   * OPEN LOCATION MODAL
   * =========================================================
   */

  async function openLocationPopup() {
    const overlay =
      document.getElementById(
        'bunker-location-overlay'
      );

    if (!overlay) {
      return;
    }

    overlay.hidden =
      false;

    document.documentElement
      .style.overflow =
      'hidden';

    const input =
      overlay.querySelector(
        '.bunker-location-input'
      );

    input.value =
      getSavedPostcode();

    hidePopupError();

    setMapResultLoading(
      'Loading map…'
    );

    try {
      await ensureMapReady();

      const initial =
        await getInitialMapPosition();

      bunkerMap.setCenter(
        initial.center
      );

      bunkerMap.setZoom(
        initial.zoom
      );

      setTimeout(
        updateMapLocationFromCenter,
        250
      );

    } catch (error) {

      console.error(
        '[Bunker] Map failed:',
        error
      );

      setMapResultError(
        'Map unavailable. You can still enter your postcode below.'
      );
    }
  }

  function closeLocationPopup() {
    const overlay =
      document.getElementById(
        'bunker-location-overlay'
      );

    if (!overlay) {
      return;
    }

    overlay.hidden =
      true;

    document.documentElement
      .style.overflow =
      '';
  }

  /*
   * =========================================================
   * INITIAL LOCATION
   * =========================================================
   */

  async function initializeLocation() {
    /*
     * Clear legacy IP data.
     */

    if (
      getLocationSource() ===
      'ip'
    ) {
      [
        STATE_KEY,
        SOURCE_KEY,
        CITY_KEY,
        COUNTRY_KEY,
        COUNTRY_CODE_KEY
      ].forEach(
        key =>
          localStorage
            .removeItem(key)
      );
    }

    const state =
      getSavedState();

    const source =
      getLocationSource();

    /*
     * Existing Australian location.
     */

    if (
      state &&
      (
        source ===
          'postcode' ||
        source ===
          'gps' ||
        source ===
          'map'
      )
    ) {
      applyRegionalAvailability(
        state
      );

      updateLocationBar();

      return;
    }

    /*
     * Previously outside AU.
     */

    if (
      source ===
      'gps_outside_au'
    ) {
      applyRegionalAvailability(
        null
      );

      updateLocationBar();

      return;
    }

    /*
     * No location yet.
     */

    applyRegionalAvailability(
      null
    );

    const attempted =
      localStorage.getItem(
        GEO_ATTEMPTED_KEY
      );

    /*
     * Location was already attempted
     * on a previous visit.
     *
     * Don't trigger browser permission
     * over and over.
     *
     * Instead show the location selector.
     */

    if (attempted) {
      updateLocationBar();

      setTimeout(
        () => {
          openLocationPopup();
        },
        400
      );

      return;
    }

    /*
     * FIRST VISIT
     *
     * Ask browser/device for location.
     *
     * If it fails, requestPreciseLocation()
     * automatically opens the modal.
     */

    await requestPreciseLocation();
  }

  /*
   * =========================================================
   * RESET FOR TESTING
   * =========================================================
   */

  function clearAllLocationData() {
    [
      STATE_KEY,
      POSTCODE_KEY,
      SOURCE_KEY,
      CITY_KEY,
      COUNTRY_KEY,
      COUNTRY_CODE_KEY,
      DETECTED_POSTCODE_KEY,
      LAT_KEY,
      LNG_KEY,
      GEO_ATTEMPTED_KEY,
      GEO_ERROR_KEY
    ].forEach(
      key =>
        localStorage
          .removeItem(key)
    );

    selectedMapLocation =
      null;

    applyRegionalAvailability(
      null
    );

    updateLocationBar();
  }

  /*
   * =========================================================
   * PUBLIC API
   * =========================================================
   */

  window
    .BunkerRegionalAvailability =
  {
    getState:
      getSavedState,

    getPostcode:
      getSavedPostcode,

    getLocation:
      getSavedLocationInfo,

    getStateFromPostcode:
      getStateFromPostcode,

    requestLocation:
      requestPreciseLocation,

    setStateFromPostcode:
      setStateFromPostcode,

    clearState:
      clearAllLocationData,

    apply:
      applyRegionalAvailability,

    updateCounts:
      updateRegionalProductCounts,

    openLocation:
      openLocationPopup,

    closeLocation:
      closeLocationPopup
  };

  /*
   * =========================================================
   * INITIAL LOAD
   * =========================================================
   */

  document.addEventListener(
    'DOMContentLoaded',
    () => {

      console.log(
        '[Bunker] Regional availability loaded - GPS + Map + Mobile Fallback'
      );

      injectStyles();

      createLocationPopup();

      createLocationBar();

      initCollectionFilterStickyOffset();

      attachStatesToProductCards();

      initializeLocation();
    }
  );

  /*
   * =========================================================
   * DYNAMIC COLLECTION SUPPORT
   * =========================================================
   */

  let observerTimer =
    null;

  const observer =
    new MutationObserver(
      () => {

        clearTimeout(
          observerTimer
        );

        observerTimer =
          setTimeout(
            () => {

              attachStatesToProductCards();

              applyRegionalAvailability(
                getSavedState()
              );

            },
            120
          );
      }
    );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

})();

